import { Drawer, Tooltip } from "antd";
import { ReactElement, useContext, useEffect, useRef, useState } from "react";
import { InfoCircleFilled } from "@ant-design/icons";
import clsx from "clsx";
import { formatNumber, getPortfolioStatus } from "src/utils/helper/functions";
import moment from "moment";
import { currenyDecimals } from "src/utils/data/constant";
import Number from "./number.component";
import { Virtual, Navigation, Pagination, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import DashboardContext from "src/context/dashboard-context";
import AccountPortfolioDetails from "./account-portfolio-details.component";

type PortfolioSwiperProps = {
  portfolioList: any;
  showDetails?: boolean;
  view: string;
};

const PortfolioSwiper: React.FC<PortfolioSwiperProps> = ({
  portfolioList,
  showDetails = false,
  view,
}: PortfolioSwiperProps): ReactElement => {
  const { selectedAccount, setSelectedPortfolio } =
    useContext(DashboardContext);
  const [slides, setSlides] = useState<any>([]);
  const [openPortfolioDetails, setOpenPortfolioDetails] = useState(false);

  useEffect(() => {
    setSlides(
      Array.from({ length: portfolioList?.length }).map((_, index) => (
        <div
          key={index}
          className="w-full flex-col justify-start items-center gap-4 inline-flex"
          onClick={() => {
            setSelectedPortfolio(portfolioList[index]);
            setOpenPortfolioDetails(true);
          }}
        >
          <div className=" self-stretch p-3 bg-[#fdfdfd] rounded-lg shadow border border-[#ececec] flex-col justify-start items-start gap-2.5 flex">
            <div className=" self-stretch justify-start items-center gap-4 inline-flex">
              <div className="w-full justify-between h-[15px] items-start gap-3 flex">
                <div className="flex items-center gap-1">
                  {portfolioList[index]?.portfolioDisclaimer && (
                    <Tooltip
                      title={
                        <ul>
                          {portfolioList[index]?.portfolioDisclaimer
                            ?.split("#")
                            ?.map((item: any, index: number) => (
                              <li key={index}>{item}</li>
                            ))}
                        </ul>
                      }
                    >
                      <InfoCircleFilled
                        className="cursor-pointer"
                        style={{ color: "#ffa500" }}
                      />
                    </Tooltip>
                  )}
                  <div className=" text-[#535353] text-[10px] md:text-xs font-semibold">
                    Portfolio number
                  </div>
                </div>
                <div className=" text-right text-[#2a2a2a] text-[10px] md:text-xs font-semibold">
                  {portfolioList[index]?.accountCode ||
                    `${selectedAccount?.accountCode}-New`}
                </div>
              </div>
            </div>
            <div className=" self-stretch justify-start items-center gap-4 inline-flex">
              <div className=" grow shrink basis-0 h-[15px] justify-start items-start gap-3 flex">
                <div className=" text-[#535353] text-[10px] md:text-xs font-semibold">
                  Portfolio status
                </div>
                <div className=" grow shrink basis-0 h-[15px] justify-end items-center gap-1 flex">
                  <div
                    className={clsx(
                      " w-2 h-2 rounded-full",
                      getPortfolioStatus(
                        selectedAccount?.portfolios[index]?.statusCode
                      ) === "Pending"
                        ? "bg-amber-500"
                        : getPortfolioStatus(
                            selectedAccount?.portfolios[index]?.statusCode
                          ) === "Blocked" ||
                          getPortfolioStatus(
                            selectedAccount?.portfolios[index]?.statusCode
                          ) === "Closed"
                        ? "bg-[#f44336]"
                        : "bg-[#00b050]"
                    )}
                  />
                  <div className=" text-right text-[#2a2a2a] text-[10px] md:text-xs font-semibold">
                    {getPortfolioStatus(portfolioList[index]?.statusCode)}
                  </div>
                </div>
              </div>
            </div>
            <div className=" self-stretch justify-start items-center gap-4 inline-flex">
              <div className=" grow shrink basis-0 h-[15px] justify-start items-start gap-3 flex">
                <div className=" text-[#535353] text-[10px] md:text-xs font-semibold">
                  Portfolio type
                </div>
                <div className=" grow shrink basis-0 h-[15px] justify-end items-center gap-2 flex">
                  <div className=" text-right text-[#2a2a2a] text-[10px] md:text-xs font-semibold">
                    {portfolioList[index]?.managementType}
                  </div>
                </div>
              </div>
            </div>
            <div className=" self-stretch justify-start items-center gap-4 inline-flex">
              <div className=" grow shrink basis-0 h-[15px] justify-start items-start gap-3 flex">
                <div className=" text-[#535353] text-[10px] md:text-xs font-semibold">
                  Portfolio open date
                </div>
                <div className=" grow shrink basis-0 h-[15px] justify-end items-center gap-2 flex">
                  <div className=" text-right text-[#2a2a2a] text-[10px] md:text-xs font-semibold">
                    {selectedAccount?.portfolios[index]?.openDate
                      ? moment(
                          selectedAccount?.portfolios[index]?.openDate
                        )?.format("MMM, DD YYYY")
                      : "-"}
                  </div>
                </div>
              </div>
            </div>
            <div className=" self-stretch h-6 justify-start items-center gap-3 inline-flex">
              <div className=" text-[#535353] text-[10px] md:text-xs font-semibold">
                Net Asset
              </div>
              <div className=" grow shrink basis-0 self-stretch justify-end items-center gap-1 flex">
                {portfolioList[index]?.portfolioMarketAmount ? (
                  <div className=" text-right">
                    <span className="text-[#2a2a2a] text-[10px] font-semibold">
                      {selectedAccount?.portfolios[index]?.portfolioCurrency}
                    </span>
                    <span className="text-[#2a2a2a] text-sm font-semibold">
                      {" "}
                    </span>
                    <span className="text-[#2a2a2a] text-[10px] md:text-xs font-semibold">
                      {formatNumber(
                        portfolioList[index]?.portfolioMarketAmount,
                        currenyDecimals[
                          portfolioList[index]?.portfolioCurrency
                        ],
                        true
                      )}
                    </span>
                  </div>
                ) : (
                  "-"
                )}
                {portfolioList[index]?.portfolioPerformancePct ? (
                  <div className="Stats p-1 bg-[#eeeeee] rounded-[32px] justify-start items-center gap-1 flex">
                    {portfolioList[index]?.portfolioPerformancePct >= 0 ? (
                      <img
                        src="/images/green-arrow.svg"
                        alt="arrow-up"
                        width={16}
                        height={16}
                      />
                    ) : (
                      <img
                        src="/images/red-arrow.svg"
                        alt="arrow-up"
                        width={16}
                        height={16}
                      />
                    )}
                    <div className="Label text-right text-[#00b050] text-[10px] md:text-xs font-medium">
                      <Number
                        number={portfolioList[index]?.portfolioPerformancePct}
                        subText="%"
                      />
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </div>
      ))
    );
  }, [portfolioList]);

  const swiperRef = useRef<any>(null);

  const onClose = () => {
    setSelectedPortfolio(null);
    setOpenPortfolioDetails(false);
  };

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      const swiper = swiperRef.current.swiper;
      if (swiper.isEnd) {
        swiper.slideTo(0);
      } else {
        swiper.slideNext();
      }
    }
  };

  const handlePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  return (
    <div
      className={clsx(
        "portfolio-swiper bg-white rounded-xl flex flex-col p-3 md:p-4 add-shadow ",
        showDetails
          ? "mt-0 grow pb-1 slide-left h-fit md:h-[260px]"
          : "mt-0 grow slide-right h-[260px]"
      )}
    >
      <div className="flex items-center justify-between w-full">
        <div className="w-fit opacity-90 text-[#2a2a2a] text-sm md:text-base font-bold">
          {view === "portfolio" ? "Portfolio details" : "Portfolios"}
        </div>
        {portfolioList?.length > 1 ? (
          <div className="custom-buttons relative w-[100px]">
            <div
              onClick={handlePrev}
              className="cursor-pointer bg-[#F5F6FA] z-10 rounded-full w-[30px] h-[30px] flex items-center justify-center absolute end-12 -top-4 "
            >
              <img
                src="/images/right-arrow.svg"
                alt="arrow-left"
                width={8}
                height={8}
                className="rotate-180"
              />
            </div>
            <div
              onClick={handleNext}
              className="cursor-pointer bg-[#F5F6FA] rounded-full w-[30px] h-[30px] flex items-center justify-center absolute end-1 -top-4 z-10 "
            >
              <img
                src="/images/right-arrow.svg"
                alt="arrow-left"
                width={8}
                height={8}
              />
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
      <Swiper
        ref={swiperRef}
        modules={[Virtual, Navigation, Pagination, Autoplay]}
        slidesPerView={
          !showDetails && portfolioList?.length > 1
            ? 1.3
            : showDetails && portfolioList?.length
            ? 1.7
            : 1
        }
        centeredSlides={false}
        spaceBetween={10}
        style={{ height: "100%" }}
        pagination={true}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        virtual
      >
        {slides.map((slideContent: any, index: number) => (
          <SwiperSlide
            key={slideContent}
            virtualIndex={index}
            // className="h-[250px]"
          >
            {slideContent}
          </SwiperSlide>
        ))}
      </Swiper>

      <Drawer
        title="Basic Drawer"
        onClose={onClose}
        width={750}
        open={openPortfolioDetails}
        destroyOnClose={true}
        className="portfolio-details-drawer"
      >
        <AccountPortfolioDetails onClose={onClose} />
      </Drawer>
    </div>
  );
};

export default PortfolioSwiper;
